import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../store'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { EmptyState } from '../components/ui/EmptyState'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import {
  SparklesIcon,
  XMarkIcon,
  CheckCircleIcon,
  PlusIcon,
  FolderIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
  BeakerIcon
} from '@heroicons/react/24/outline'
import { formatDistanceToNow } from 'date-fns'
import { getProjectsRequest } from '../store/actions/projectActions'
import { projectService } from '../services/projectService'
import { authService } from '../services/authService'
import toast from 'react-hot-toast'

interface Project {
  id: string
  name: string
  description?: string
  status: string
  createdAt: string
  updatedAt: string
  metadata?: {
    id: string
  }
}

interface WorkflowStatus {
  isCreating: boolean
  isFailed: boolean
  isComplete: boolean
  progress?: number
}

interface TokenUsage {
  planType: 'free' | 'starter' | 'intelligence'
  tokensTotal: number
  tokensRemaining: number
  tokensUsed: number
  projectsAllowed: number
  dailyLimit: number
  dailyUsed: number
  dailyRemaining: number
  canTopUp: boolean
  limitCondition: string
  lastResetAt: string | null
  percentageUsed: number
}

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { projects, isLoading } = useSelector((state: RootState) => state.projects)
  const { user } = useSelector((state: RootState) => state.auth)
  const planType = (user as any)?.planType || 'free'

  const [projectStatuses] = useState<Record<string, WorkflowStatus>>({})
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const [limitModalOpen, setLimitModalOpen] = useState(false)
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; projectId: string | null }>({
    isOpen: false,
    projectId: null,
  })
  const [tokenUsage, setTokenUsage] = useState<TokenUsage | null>(null)
  const [loadingTokenUsage, setLoadingTokenUsage] = useState(true)

  const hasReachedProjectLimit = tokenUsage && tokenUsage.projectsAllowed < 999
    ? (projects.length >= (tokenUsage.projectsAllowed || 1))
    : false

  useEffect(() => {
    dispatch(getProjectsRequest())
    fetchTokenUsage()
  }, [dispatch])

  const fetchTokenUsage = async () => {
    try {
      setLoadingTokenUsage(true)
      const response = await authService.getTokenUsage()
      if (response.success && response.data) {
        setTokenUsage(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch token usage:', error)
    } finally {
      setLoadingTokenUsage(false)
    }
  }

  const navigateToProject = async (project: Project) => {
    navigate(`/projects/${project.id}`, {
      state: {
        project,
        breadcrumb: {
          name: project.name,
          link: '/dashboard',
          icon: 'folder',
        },
      },
    })
  }

  const navigateToCreateProject = () => {
    if (hasReachedProjectLimit) {
      setLimitModalOpen(true)
      return
    }
    navigate('/projects/create')
  }

  const handleEdit = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    setActiveMenu(null)
    navigate(`/projects/${id}`, { state: { editCore: true } })
  }

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    setDeleteModal({ isOpen: true, projectId: id })
    setActiveMenu(null)
  }

  const confirmDelete = async () => {
    if (!deleteModal.projectId) return

    try {
      await projectService.deleteProject(deleteModal.projectId)
      dispatch(getProjectsRequest())
      setDeleteModal({ isOpen: false, projectId: null })
    } catch (error) {
      console.error('Failed to delete project:', error)
      alert('Failed to delete project')
    }
  }

  const getBadgeConfig = (status: WorkflowStatus | null) => {
    if (!status) return null

    if (status.isCreating) {
      return {
        text: 'Creating...',
        variant: 'secondary' as const,
        icon: SparklesIcon,
      }
    }

    if (status.isFailed) {
      return {
        text: 'Failed',
        variant: 'destructive' as const,
        icon: XMarkIcon,
      }
    }

    if (status.isComplete) {
      return {
        text: 'Ready',
        variant: 'success' as const,
        icon: CheckCircleIcon,
      }
    }

    return null
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
           <div className="relative">
             <div className="absolute inset-0 rounded-full border-t-2 border-primary animate-spin" />
             <div className="w-12 h-12 rounded-full border-2 border-primary/20 backdrop-blur-sm" />
           </div>
           <span className="text-xs font-bold text-primary uppercase tracking-widest animate-pulse">Initializing Agent...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4sm:px-0 animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6" onClick={() => setActiveMenu(null)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white uppercase tracking-wide">Project Command Center</h1>
          <p className="text-text-secondary mt-1 text-sm font-medium">Manage your workflows and active tasks</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={navigateToCreateProject}
            className="flex items-center gap-2 rounded-xl px-5 py-2.5 transition-all bg-primary hover:bg-orange-600 text-white shadow-[0_0_15px_rgba(255,107,0,0.3)] hover:shadow-[0_0_25px_rgba(255,107,0,0.5)] hover:-translate-y-0.5 border border-primary/50 font-black uppercase text-xs tracking-wider"
          >
            <PlusIcon className="h-4 w-4" strokeWidth={3} />
            New Project
          </Button>
        </div>
      </div>

      {/* AI Token Usage Card - Agentic Redesign */}
      <div className="glass-panel relative overflow-hidden group border border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.3)] backdrop-blur-2xl p-6">
        {/* Glow Effects */}
        <div className="absolute -top-32 -right-32 w-64 h-64 bg-primary/10 rounded-full blur-[80px] pointer-events-none group-hover:bg-primary/20 transition-all duration-1000" />
        
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              {/* Agent Status Bubble */}
              <div className="absolute inset-0 bg-primary/20 rounded-full blur animate-agent-glow" />
              <div className="relative p-2.5 bg-black/40 border border-primary/30 rounded-full backdrop-blur-xl flex items-center justify-center">
                <SparklesIcon className="h-5 w-5 text-primary" strokeWidth={2} />
              </div>
            </div>
            <div>
              <h2 className="text-lg font-black text-white flex items-center gap-2 tracking-tight">
                Resource Usage
                <div className="flex items-center gap-1 bg-black/40 border border-white/5 py-0.5 px-2 rounded font-mono text-[9px] text-info uppercase tracking-widest shadow-inner">
                  <span className="w-1.5 h-1.5 bg-info rounded-full animate-pulse-subtle" />
                  Live Sync
                </div>
              </h2>
              <p className="text-xs text-text-secondary mt-0.5 capitalize font-medium flex items-center gap-1.5">
                <span className="bg-primary/20 text-primary px-1.5 py-0.5 rounded text-[10px] font-bold uppercase">{tokenUsage?.planType || 'Free'}</span> Plan Tier
              </p>
            </div>
          </div>
          <button
            onClick={fetchTokenUsage}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors border border-white/0 hover:border-white/10"
            title="Refresh telemetries"
          >
            <SparklesIcon className="h-4 w-4 text-text-secondary" />
          </button>
        </div>

        {loadingTokenUsage ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : tokenUsage ? (
          <div className="space-y-6">
            {/* Stats Grid - High Density Agentic Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { title: "Tokens Left", val: tokenUsage.tokensRemaining?.toLocaleString() ?? '0', sub: `of ${tokenUsage.tokensTotal?.toLocaleString() ?? '0'}`, color: "text-green-400" },
                { title: "Tokens Burned", val: tokenUsage.tokensUsed?.toLocaleString() ?? '0', sub: "consumed overall", color: "text-primary" },
                { title: "Daily Limit Left", val: tokenUsage.dailyRemaining?.toLocaleString() ?? '0', sub: `of ${tokenUsage.dailyLimit?.toLocaleString() ?? '0'} today`, color: "text-info" },
                { title: "Active Agents", val: tokenUsage.projectsAllowed >= 999 ? '∞' : tokenUsage.projectsAllowed, sub: "max allowed slots", color: "text-purple-400" },
              ].map((stat, i) => (
                <div key={i} className="bg-black/40 backdrop-blur-md rounded-xl p-4 border border-white/10 relative overflow-hidden group/card hover:border-white/20 transition-colors">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity" />
                  <p className="text-[10px] font-black text-text-secondary uppercase tracking-widest mb-1">{stat.title}</p>
                  <p className={`text-2xl font-black ${stat.color} filter drop-shadow-md`}>{stat.val}</p>
                  <p className="text-[10px] text-text-secondary/80 font-medium mt-1 uppercase tracking-wider">{stat.sub}</p>
                </div>
              ))}
            </div>

            {/* Total Token Progress Bar */}
            <div className="bg-black/30 p-4 rounded-xl border border-white/5 space-y-3">
              <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider">
                <span className="text-text-secondary">Network Saturation</span>
                <span className="text-primary">{tokenUsage.percentageUsed}%</span>
              </div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ease-out rounded-full shadow-[0_0_10px_currentColor] ${
                    tokenUsage.percentageUsed >= 90
                      ? 'bg-gradient-to-r from-red-600 to-red-400 text-red-500'
                      : tokenUsage.percentageUsed >= 70
                        ? 'bg-gradient-to-r from-orange-600 to-primary text-primary'
                        : 'bg-gradient-to-r from-green-600 to-emerald-400 text-emerald-500'
                  }`}
                  style={{ width: `${Math.min(tokenUsage.percentageUsed, 100)}%` }}
                />
              </div>
            </div>

            {/* Plan CTA */}
            <div className="pt-2">
              {tokenUsage.canTopUp ? (
                tokenUsage.tokensRemaining <= 0 ? (
                  <div className="glass-panel p-4 border border-purple-500/30 shadow-[0_0_20px_rgba(168,85,247,0.15)] flex items-center justify-between">
                    <div>
                      <p className="font-black text-sm text-purple-400 mb-0.5 uppercase tracking-wider flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-error animate-pulse" />
                        Resources Depleted
                      </p>
                      <p className="text-[11px] text-text-secondary font-medium uppercase tracking-wider">Acquire Top-up — $30 → +5M Tokens</p>
                    </div>
                    <Button
                      onClick={() => navigate('/profile?tab=billing')}
                      variant="secondary"
                      className="bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/40 text-xs font-black uppercase tracking-wider"
                    >
                      Initialize Transfer
                    </Button>
                  </div>
                ) : null
              ) : (
                <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 flex items-center justify-between shadow-[0_0_15px_rgba(255,107,0,0.1)] hover:shadow-[0_0_25px_rgba(255,107,0,0.2)] transition-shadow">
                  <div>
                    <p className="font-black text-primary text-sm mb-0.5 uppercase tracking-wider">
                      {tokenUsage.tokensRemaining <= 0 ? 'Agent Network Halted' : 'Expand Agent Capability'}
                    </p>
                    <p className="text-[11px] text-primary/70 font-medium">
                      {tokenUsage.planType === 'free'
                        ? 'Upgrade to Starter ($20) to unlock 1M tokens & 3 sub-agents'
                        : 'Upgrade to Intelligence ($49) to unlock 5M tokens & unlimited sub-agents'}
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/subscription-pricing')}
                    className="bg-primary hover:bg-orange-600 text-white shadow-[0_0_10px_rgba(255,107,0,0.4)] border-none text-xs font-black uppercase tracking-widest px-6"
                  >
                    Upgrade Capability
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-text-secondary text-sm font-medium">Failed to fetch data</p>
            <button onClick={fetchTokenUsage} className="text-primary text-xs font-bold uppercase tracking-widest mt-2 hover:underline">Reboot Connection</button>
          </div>
        )}
      </div>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <EmptyState
          icon={FolderIcon}
          title="No active agents"
          description="Initialize a new project to launch a task agent."
          action={
            <Button onClick={navigateToCreateProject} className="bg-primary hover:bg-orange-600 text-white shadow-[0_0_15px_rgba(255,107,0,0.4)]">
              New Project
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((project) => {
            const status = projectStatuses[project.id]
            const badgeConfig = getBadgeConfig(status)
            const reqs = (project as any).requirements || []
            const isVerified = (type: string) => {
              const typeReqs = reqs.filter((r: any) => r.type === type)
              return typeReqs.length > 0 && typeReqs.every((r: any) => r.isVerified)
            }
            const allVerified = isVerified('BRD') && isVerified('PRD') && isVerified('NFR') && isVerified('UIR')

            return (
              <div
                key={project.id}
                className="glass-panel group relative border border-white/10 hover:border-primary/40 cursor-pointer overflow-hidden backdrop-blur-xl bg-card/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,107,0,0.15)] hover:-translate-y-1"
                onClick={() => navigateToProject(project)}
              >
                {/* Active agent top gradient line */}
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/50 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                
                <div className="p-5 flex flex-col h-full min-h-[160px] justify-between">
                  <div className="relative z-10 w-full">
                    <div className="flex items-start justify-between gap-2">
                       <h3 className="text-lg font-bold text-white tracking-tight line-clamp-1 flex-1">
                         {project.name}
                       </h3>
                       {/* Options Menu Toggle */}
                       <button
                         onClick={(e) => {
                           e.stopPropagation()
                           setActiveMenu(activeMenu === project.id ? null : project.id)
                         }}
                         className="p-1 rounded-md text-text-secondary hover:text-white hover:bg-white/10 transition-colors"
                       >
                         <EllipsisVerticalIcon className="h-5 w-5" />
                       </button>

                       {/* Dropdown Menu */}
                       {activeMenu === project.id && (
                         <div
                           className="absolute right-0 top-8 w-32 bg-card border border-white/10 rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.5)] py-1.5 z-20 animate-in fade-in zoom-in duration-75 backdrop-blur-2xl"
                           onClick={(e) => e.stopPropagation()}
                         >
                           <button
                             disabled={allVerified}
                             onClick={(e) => handleEdit(e, project.id)}
                             className={`flex items-center w-full px-3 py-1.5 text-xs font-bold transition-colors gap-2 ${
                               allVerified
                                 ? 'text-text-secondary/30 cursor-not-allowed opacity-50'
                                 : 'text-text-secondary hover:bg-white/10 hover:text-white'
                             }`}
                           >
                             <PencilSquareIcon className="h-4 w-4" /> Edit Core
                           </button>
                         </div>
                       )}
                    </div>
                    {project.description && (
                      <p className="text-xs text-text-secondary font-medium mt-1 line-clamp-2 pr-4 leading-relaxed">
                        {project.description}
                      </p>
                    )}
                  </div>

                  <div className="mt-6 flex items-center justify-between border-t border-white/5 pt-3 relative z-10">
                     <div className="flex items-center gap-2">
                       <span className="relative flex h-2 w-2">
                         <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${project.status.toLowerCase() === 'active' || project.status.toLowerCase() == 'completed' || project.status === 'Ready' ? 'bg-primary' : 'bg-info'}`}></span>
                         <span className={`relative inline-flex rounded-full h-2 w-2 ${project.status.toLowerCase() === 'active' || project.status.toLowerCase() == 'completed' || project.status === 'Ready' ? 'bg-primary' : 'bg-info shadow-[0_0_5px_theme(colors.info)]'}`}></span>
                       </span>
                       <span className="text-[10px] font-black uppercase tracking-widest text-white/80">
                         {project.status}
                       </span>
                     </div>
                     <span className="text-[10px] font-bold text-text-secondary tracking-wider uppercase">
                       {formatDistanceToNow(new Date(project.updatedAt), { addSuffix: true })}
                     </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <ConfirmationModal
        isOpen={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        onConfirm={() => navigate('/payment')}
        title="Project Limit Reached"
        message="Your project limit is reached. Upgrade your plan to create more projects and unlock premium features!"
        confirmLabel="Yes, Upgrade"
        cancelLabel="No, Stay on Dashboard"
        variant="info"
      />

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, projectId: null })}
        onConfirm={confirmDelete}
        title="Delete Project"
        message="Are you sure you want to delete this project? All associated requirements and data will be permanently removed. This action cannot be undone."
        confirmLabel="Delete Project"
        variant="danger"
      />
    </div>
  )
}